/**
 * Campaigns side by side, sortable, with rates rather than counts.
 *
 * This is the row of the redesign that carries the argument. The aggregate
 * cannot show that one send is failing — averaging is the whole business of an
 * aggregate — so the comparison has to be a first-class view rather than a
 * drill-down somebody thinks to try.
 *
 * Built from `.oc-table`'s divs and a `--oc-cols` grid, not from a `<table>`.
 * That is not a stylistic preference: `.oc-table__head` and `.oc-table__row`
 * are `display: grid` with `grid-template-columns: var(--oc-cols)`, so real
 * table markup leaves the custom property undefined, the grid falls back to a
 * single column, and every cell becomes its own row. The first version of this
 * file did exactly that and rendered fourteen campaigns 2,856 pixels tall.
 *
 * Sorting is client-side and that is a real constraint, stated here rather than
 * discovered: the endpoint returns the loudest groups by volume, capped. Sorting
 * the returned page by bounce rate finds the worst of the *biggest* sends, not
 * the worst overall. For a comparison view that is the right trade — a
 * catastrophic rate on eleven messages is not the problem — but it is not the
 * same as sorting the whole history, and the footer says so.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Icon } from "../components/Sprites";
import { formatRate, healthOf, rate, THRESHOLDS } from "./rates";
import type { AnalyticsGroup, BreakdownDimension } from "../api/types";

type SortKey = "sent" | "delivered" | "opened" | "clicked" | "bounced" | "last";

/**
 * The last column is unsubscribes for a send and complaints for a mailbox
 * provider. An unsubscribe is about the list, and says nothing about one
 * provider; a complaint is what a provider weighs, and the per-provider rate is
 * where it shows that Microsoft's users are pressing "junk" while Gmail's are
 * not.
 */
function lastColumn(dimension: BreakdownDimension) {
  return dimension === "mailbox_provider"
    ? ({ label: "Complaints", help: "Complaints as a share of sent.", field: "complained", thresholds: THRESHOLDS.complaint } as const)
    : ({ label: "Unsub.", help: "Unsubscribes as a share of sent.", field: "unsubscribed", thresholds: THRESHOLDS.unsubscribe } as const);
}

interface BreakdownTableProps {
  groups: AnalyticsGroup[];
  dimension: BreakdownDimension;
  /** Below this, rates are not scored — one bounce in three is not 33% of anything. */
  volumeFloor?: number;
}

export function BreakdownTable({
  groups,
  dimension,
  volumeFloor = 50,
}: BreakdownTableProps) {
  const [sort, setSort] = useState<SortKey>("sent");
  const [descending, setDescending] = useState(true);
  const last = lastColumn(dimension);

  const rows = useMemo(() => {
    const scored = groups.map((group) => {
      const t = group.totals;
      return {
        group,
        sent: t.sent,
        delivered: rate(t.delivered, t.sent),
        // Messages, not events, as the funnel counts them.
        opened: rate(t.unique.opened, t.delivered),
        clicked: rate(t.unique.clicked, t.delivered),
        bounced: rate(t.bounced, t.sent),
        last: rate(t[last.field], t.sent),
      };
    });

    return scored.sort((a, b) => {
      const left = sort === "sent" ? a.sent : a[sort];
      const right = sort === "sent" ? b.sent : b[sort];

      // A null rate has no denominator, so it is not "worse than zero" — it is
      // unknown. Sorting it to the bottom either way keeps rows with no basis
      // for comparison out of the position that means "look here".
      if (left === null && right === null) return 0;
      if (left === null) return 1;
      if (right === null) return -1;

      return descending ? right - left : left - right;
    });
  }, [groups, sort, descending, last.field]);

  if (groups.length === 0) {
    return (
      <div className="oc-empty">
        <div className="oc-empty__title">Nothing sent in this period</div>
        <div className="oc-empty__hint">
          Groups with no events are left out rather than listed with zeroes.
        </div>
      </div>
    );
  }

  const heading = (key: SortKey, label: string, help?: string) => (
    <button
      type="button"
      className={`breakdown__sort${sort === key ? " breakdown__sort--active" : ""}`}
      title={help}
      aria-label={`Sort by ${label}`}
      onClick={() => {
        if (sort === key) {
          setDescending((d) => !d);
        } else {
          setSort(key);
          setDescending(true);
        }
      }}
    >
      {label}
      {sort === key && (
        <Icon name="chevron" size={10} className={descending ? "caret" : "caret caret--up"} />
      )}
    </button>
  );

  return (
    <>
      <div className="oc-table breakdown-table">
        <div className="oc-table__head" role="row">
          <span>{DIMENSION_NOUN[dimension].heading}</span>
          <span className="oc-table__cell--right">{heading("sent", "Sent")}</span>
          <span className="oc-table__cell--right">
            {heading("delivered", "Delivered", "Delivered as a share of sent.")}
          </span>
          <span className="oc-table__cell--right">
            {heading("opened", "Opened", "Messages opened, as a share of delivered, including MPP. A click counts as an open.")}
          </span>
          <span className="oc-table__cell--right">
            {heading("clicked", "Clicked", "Messages clicked, as a share of delivered.")}
          </span>
          <span className="oc-table__cell--right">
            {heading(
              "bounced",
              "Bounced",
              `Warns at ${formatRate(THRESHOLDS.bounce.warn)}, pauses sending at ${formatRate(THRESHOLDS.bounce.critical!)}.`,
            )}
          </span>
          <span className="oc-table__cell--right">{heading("last", last.label, last.help)}</span>
        </div>

        <div className="oc-table__body">
          {rows.map((row) => {
            const { group } = row;
            // Below the floor a rate is arithmetic without meaning, so it is
            // shown and not coloured. Colouring it would make a single bounce
            // on a two-message test send look like an incident.
            const scored = row.sent >= volumeFloor;
            const bounceHealth =
              scored && row.bounced !== null ? healthOf(row.bounced, THRESHOLDS.bounce) : "ok";
            const lastHealth =
              scored && row.last !== null ? healthOf(row.last, last.thresholds) : "ok";

            return (
              <div
                className="oc-table__row breakdown__row"
                role="row"
                key={group.key ?? "no-key"}
              >
                <span className="breakdown__name">{label(group, dimension)}</span>
                <span className="oc-table__cell--right oc-mono-sm">
                  {row.sent.toLocaleString()}
                </span>
                <span className="oc-table__cell--right oc-mono-sm">
                  {formatRate(row.delivered)}
                </span>
                <span className="oc-table__cell--right oc-mono-sm">
                  {formatRate(row.opened)}
                </span>
                <span className="oc-table__cell--right oc-mono-sm">
                  {formatRate(row.clicked)}
                </span>
                <span className={`oc-table__cell--right oc-mono-sm rate--${bounceHealth}`}>
                  {formatRate(row.bounced)}
                </span>
                <span className={`oc-table__cell--right oc-mono-sm rate--${lastHealth}`}>
                  {formatRate(row.last)}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
        {dimension === "mailbox_provider"
          ? "Email in this period by the provider that filters the recipient's mail."
          : `The busiest ${groups.length} ${DIMENSION_NOUN[dimension].plural} in this period, sorted here in the browser — so a rate column finds the worst of these rather than the worst ever sent.`}{" "}
        Rates are only scored against thresholds above {volumeFloor} messages; below that a
        single bounce moves the number too far to mean anything.
      </p>
    </>
  );
}

const DIMENSION_NOUN: Record<BreakdownDimension, { heading: string; plural: string; deleted: string }> = {
  campaign: { heading: "Campaign", plural: "campaigns", deleted: "campaign" },
  template: { heading: "Template version", plural: "template versions", deleted: "template version" },
  mailbox_provider: { heading: "Mailbox provider", plural: "mailbox providers", deleted: "provider" },
};

function label(group: AnalyticsGroup, dimension: BreakdownDimension) {
  if (dimension === "mailbox_provider") {
    // The server names every bucket it knows. A key without a name is one the
    // server added later than this screen, and reads as itself.
    if (!group.key) {
      return (
        <>
          <strong>Not recorded</strong>
          <span className="oc-text-secondary"> — older mail</span>
        </>
      );
    }
    return <span>{group.name ?? group.key}</span>;
  }

  if (!group.key) {
    return (
      <>
        <strong>Transactional</strong>
        <span className="oc-text-secondary"> — sent through the API, no campaign</span>
      </>
    );
  }

  if (!group.name) {
    // The subject was deleted after its events were recorded. The numbers are
    // still real, so the row stays; inventing a name would be worse.
    return (
      <span className="oc-text-secondary">
        Deleted {DIMENSION_NOUN[dimension].deleted}{" "}
        <span className="oc-mono-sm">{group.key.slice(0, 8)}</span>
      </span>
    );
  }

  return dimension === "campaign" ? (
    <Link to={`/campaigns/${group.key}`}>{group.name}</Link>
  ) : (
    <span>{group.name}</span>
  );
}
